import React, { useState, useEffect, useRef } from "react";
import "./App.css";

// ── RIS BRAND TOKENS ──────────────────────────────────────────────────────────
const B = {
  bg:        "#0A1628",
  bgDeep:    "#060F1A",
  surface:   "#0F2040",
  surfaceHi: "#162B52",
  border:    "#1A3460",
  borderHi:  "#2450A0",
  blue:      "#2E86DE",
  blueHi:    "#4FA3F7",
  blueDim:   "#1A5099",
  bluePale:  "#0D2848",
  white:     "#FFFFFF",
  textSub:   "#8BA3BF",
  textDim:   "#4A6080",
  textMuted: "#6A85A0",
  green:     "#1DB954",
  greenPale: "#0A2818",
  yellow:    "#F5A623",
  yellowPale:"#2A1E08",
  red:       "#E5383B",
  redPale:   "#2A0A0A",
};

const FONT        = "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700;800&family=Barlow:wght@300;400;500;600&display=swap";
const VIDEO_URL   = "https://youtu.be/RrnN4xuFFOo?si=0TK6mIIm6zVnnjqt";
const BOOKING_URL = "https://vault.unlimitedlifeconcepts.com/book-with-kevin";

// ── MATH ENGINE (IBC-agnostic) ────────────────────────────────────────────────
function scoreSystem(d) {
  // income entered is gross; approximate take-home at ~72% for scoring purposes
  const grossIncome = parseFloat(d.income) || 0;
  const income      = grossIncome * 0.72;

  const housing     = parseFloat(d.housing) || 0;
  const utilities   = parseFloat(d.utilities) || 0;
  const food        = parseFloat(d.food) || 0;
  const transport   = parseFloat(d.transport) || 0;
  const insurance   = parseFloat(d.insurance) || 0;
  const debtPmt     = parseFloat(d.debtPmt) || 0;
  const disc        = parseFloat(d.disc) || 0;
  const otherExp    = parseFloat(d.otherExp) || 0;
  const totalExp    = housing + utilities + food + transport + insurance + debtPmt + disc + otherExp;
  const surplus     = income - totalExp;

  const checking    = parseFloat(d.checking) || 0;
  const investments = parseFloat(d.investments) || 0;
  const retirement  = parseFloat(d.retirement) || 0;
  const otherAssets = parseFloat(d.otherAssets) || 0;
  const totalSaved  = checking + investments + retirement + otherAssets || 1;

  const mortgage     = parseFloat(d.mortgage) || 0;
  const creditCards  = parseFloat(d.creditCards) || 0;
  const autoLoans    = parseFloat(d.autoLoans) || 0;
  const studentLoans = parseFloat(d.studentLoans) || 0;
  const termLife     = parseFloat(d.termLife) || 0;
  const existingDB   = parseFloat(d.existingDB) || 0;
  const dependents   = parseInt(d.dependents) || 2;
  const totalDebt    = mortgage + creditCards + autoLoans + studentLoans;

  // Use gross income for protection need (insure what you actually earn)
  const protectionNeed  = (grossIncome * 12 * 10) + totalDebt + (dependents * 75000);

  // PROBLEM 1: THE DEBT DRAIN
  const debtBurden    = income > 0 ? debtPmt / income : 0;
  const annualInterest = (creditCards * 0.22) + (autoLoans * 0.075) + (mortgage * 0.065 * 0.55);
  const debtSeverity  = debtBurden > 0.35 ? "critical" : debtBurden > 0.20 ? "significant" : debtBurden > 0.10 ? "moderate" : "low";
  const debtScore     = Math.max(5, Math.min(100, (1 - Math.min(1, debtBurden * 2.5)) * 100));

  // PROBLEM 2: THE LIQUIDITY TRAP
  const liquid        = checking;
  const monthsCovered = totalExp > 0 ? liquid / totalExp : 0;
  const lockedPct     = retirement / totalSaved;
  let liqScore = 0;
  if (monthsCovered >= 6)      liqScore = 90 + Math.min(10, (monthsCovered - 6) * 2);
  else if (monthsCovered >= 3) liqScore = 65 + ((monthsCovered - 3) / 3) * 25;
  else if (monthsCovered >= 1) liqScore = 35 + ((monthsCovered - 1) / 2) * 30;
  else                         liqScore = Math.max(5, monthsCovered * 35);
  liqScore = Math.min(100, liqScore);
  const liqSeverity = monthsCovered < 1 ? "critical" : monthsCovered < 3 ? "significant" : monthsCovered < 6 ? "moderate" : "low";

  // PROBLEM 3: THE PROTECTION GAP
  const currentCoverage = termLife + existingDB;
  const protectionGap   = Math.max(0, protectionNeed - currentCoverage);
  const gapPct          = protectionNeed > 0 ? protectionGap / protectionNeed : 0;
  const protSeverity    = gapPct > 0.6 ? "critical" : gapPct > 0.3 ? "significant" : gapPct > 0.1 ? "moderate" : "low";
  const protScore       = Math.max(5, Math.min(100, (1 - gapPct) * 100));

  // OVERALL
  const overall = Math.round((debtScore * 0.35) + (liqScore * 0.35) + (protScore * 0.30));

  // CONVENTIONAL INSIGHTS — no IBC language
  const insights = [];
  if (debtBurden > 0.30)
    insights.push({ type: "bad",  text: `${(debtBurden * 100).toFixed(0)}% of your take-home income is already committed to lenders before you make a single decision with it. That's not a spending problem — it's a structural one.` });
  else if (debtBurden > 0.15)
    insights.push({ type: "warn", text: `About ${(debtBurden * 100).toFixed(0)}% of your monthly income goes to debt payments. Every dollar paid in interest is a dollar that can't build your financial position.` });

  if (monthsCovered < 1)
    insights.push({ type: "bad",  text: `Your accessible cash covers less than one month of expenses. One unexpected event — job loss, medical bill, car repair — and the entire system comes under pressure.` });
  else if (monthsCovered < 3)
    insights.push({ type: "warn", text: `You have ${monthsCovered.toFixed(1)} months of truly liquid coverage. Most financial professionals recommend at least 3 — and even that has limits.` });

  if (lockedPct > 0.6)
    insights.push({ type: "warn", text: `${Math.round(lockedPct * 100)}% of your saved capital is in retirement accounts you can't access without penalties. You have assets — but not control over them.` });

  if (protectionGap > 200000)
    insights.push({ type: "bad",  text: `There's a ${fmt(protectionGap)} gap between what your family would need and what's currently in place. That gap exists today — not someday. And it's based on replacing your full pre-tax income, which is what your household actually depends on.` });

  if (surplus < 0)
    insights.push({ type: "bad",  text: `Your expenses currently exceed your estimated take-home income. Each month the gap widens — and the options narrow.` });
  else if (income > 0 && surplus / income < 0.10)
    insights.push({ type: "warn", text: `After expenses, you're retaining about ${((surplus / income) * 100).toFixed(0)}% of your estimated take-home. There's room in that number — but only if the structure changes.` });

  if (!insights.length)
    insights.push({ type: "info", text: `Your numbers look functional on the surface. But functional and optimized are two very different things. The question is whether your money is doing everything it could be doing.` });

  const expRatios = [
    { label: "Housing",        v: housing },
    { label: "Utilities",      v: utilities },
    { label: "Food",           v: food },
    { label: "Transportation", v: transport },
    { label: "Insurance",      v: insurance },
    { label: "Debt Payments",  v: debtPmt },
    { label: "Discretionary",  v: disc },
    { label: "Other",          v: otherExp },
  ].filter(e => e.v > 0).map(e => ({ ...e, pct: income > 0 ? (e.v / income) * 100 : 0 }));

  return {
    overall,
    debtScore:  Math.round(debtScore),
    liqScore:   Math.round(liqScore),
    protScore:  Math.round(protScore),
    debtBurden, debtSeverity, annualInterest,
    monthsCovered, liqSeverity, lockedPct,
    protectionGap, protSeverity, protectionNeed, currentCoverage,
    surplus, totalExp, totalDebt,
    insights, expRatios,
    dependents,
    debtPmt,
    grossIncome,
  };
}

const fmt  = n => n >= 1000000 ? `$${(n/1000000).toFixed(2)}M` : n >= 1000 ? `$${Math.round(n/1000)}K` : `$${Math.round(n)}`;
const fmtF = n => `$${Math.round(n).toLocaleString()}`;

// ── SHARED UI ─────────────────────────────────────────────────────────────────
function RISLogo({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <polygon points="16,2 30,10 30,22 16,30 2,22 2,10" stroke="#FFFFFF" strokeWidth="2" fill="none"/>
      <polygon points="16,7 25,12 25,20 16,25 7,20 7,12" fill="#FFFFFF" opacity="0.12"/>
      <polygon points="16,10 22,14 22,20 16,24 10,20 10,14" fill="#FFFFFF" opacity="0.35"/>
      <circle cx="16" cy="17" r="3" fill="#FFFFFF"/>
    </svg>
  );
}

function Header({ subtitle }) {
  return (
    <div style={{ background: B.bgDeep, borderBottom: `1px solid ${B.border}`, padding: "0 clamp(16px,4vw,28px)" }}>
      <div style={{ maxWidth: "680px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: "52px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "11px" }}>
          <RISLogo size={24}/>
          <div>
            <div style={{ fontSize: "11px", letterSpacing: "0.16em", textTransform: "uppercase", color: B.blue, fontWeight: "600", fontFamily: "'Barlow',sans-serif", lineHeight: 1 }}>
              Richey Insurance Solutions
            </div>
            {subtitle && (
              <div style={{ fontSize: "11px", color: B.textMuted, fontFamily: "'Barlow',sans-serif", lineHeight: 1.3, marginTop: "1px" }}>
                {subtitle}
              </div>
            )}
          </div>
        </div>
        <div style={{ fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: B.textDim, fontFamily: "'Barlow',sans-serif" }}>
          Your Wealth, Your Way
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ step, total }) {
  return (
    <div style={{ display: "flex", gap: "4px", marginBottom: "28px" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ flex: 1, height: "2px", borderRadius: "1px", background: i <= step ? B.blue : B.border, transition: "background 0.3s" }}/>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, hint, prefix = "$", type = "number", placeholder }) {
  const [f, setF] = useState(false);
  return (
    <div style={{ marginBottom: "13px" }}>
      <label style={{ display: "block", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: B.textMuted, marginBottom: "5px", fontFamily: "'Barlow',sans-serif", fontWeight: "500" }}>
        {label}{hint && <span style={{ color: B.textDim, marginLeft: "5px", textTransform: "none", letterSpacing: 0 }}>· {hint}</span>}
      </label>
      <div style={{ position: "relative" }}>
        {prefix && (
          <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "13px", color: f ? B.blue : B.textDim, transition: "color .15s" }}>
            {prefix}
          </span>
        )}
        <input
          type={type} min="0" value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setF(true)} onBlur={() => setF(false)}
          placeholder={placeholder || "0"}
          style={{ width: "100%", background: B.bgDeep, border: `1px solid ${f ? B.blue : B.border}`, borderRadius: "5px", padding: `9px 10px 9px ${prefix ? "22px" : "10px"}`, color: B.white, fontSize: "14px", fontFamily: "'Barlow',sans-serif", outline: "none", boxSizing: "border-box", transition: "border-color .15s" }}
        />
      </div>
    </div>
  );
}

function Card({ children, style, accent }) {
  return (
    <div style={{ background: B.surface, border: `1px solid ${accent ? B.borderHi : B.border}`, borderRadius: "8px", padding: "20px", position: "relative", overflow: "hidden", ...style }}>
      {accent && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, ${B.blue}, transparent)` }}/>}
      {children}
    </div>
  );
}

function SeverityBadge({ severity }) {
  const map = {
    critical:    [B.redPale,    B.red,    "Critical"],
    significant: [B.yellowPale, B.yellow, "Significant"],
    moderate:    [B.bluePale,   B.blue,   "Moderate"],
    low:         [B.greenPale,  B.green,  "Strong"],
  };
  const [bg, col, label] = map[severity] || map.moderate;
  return (
    <span style={{ background: bg, color: col, fontSize: "10px", fontWeight: "600", padding: "3px 10px", borderRadius: "3px", fontFamily: "'Barlow',sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function InsightPill({ text, type }) {
  const map = {
    bad:  [B.redPale,    B.red],
    warn: [B.yellowPale, B.yellow],
    info: [B.bluePale,   B.blue],
    good: [B.greenPale,  B.green],
  };
  const [bg, col] = map[type] || map.info;
  return (
    <div style={{ background: bg, borderLeft: `3px solid ${col}`, borderRadius: "4px", padding: "11px 14px", fontSize: "13px", color: B.white, lineHeight: "1.65", marginBottom: "8px", fontFamily: "'Barlow',sans-serif" }}>
      {text}
    </div>
  );
}

function ScoreRing({ score, label, size = 100 }) {
  const r = 38, circ = 2 * Math.PI * r;
  const [anim, setAnim] = useState(0);
  useEffect(() => { const t = setTimeout(() => setAnim(score), 150); return () => clearTimeout(t); }, [score]);
  const color = score >= 75 ? B.green : score >= 50 ? B.yellow : B.red;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "7px" }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={B.border} strokeWidth="6"/>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={circ} strokeDashoffset={circ - (anim/100)*circ}
            strokeLinecap="round" style={{ transition: "stroke-dashoffset 1.1s cubic-bezier(0.34,1.56,0.64,1)" }}/>
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: size > 105 ? "26px" : "20px", fontWeight: "700", color: B.white, fontFamily: "'Barlow Condensed',sans-serif" }}>
            {Math.round(anim)}
          </span>
        </div>
      </div>
      <span style={{ fontSize: "10px", color: B.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'Barlow',sans-serif" }}>
        {label}
      </span>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [phase, setPhase]     = useState("landing");
  const [stepIdx, setStepIdx] = useState(1);
  const [result, setResult]   = useState(null);
  const topRef = useRef(null);


  const [d, setD] = useState({
    income: "", housing: "", utilities: "", food: "", transport: "",
    insurance: "", debtPmt: "", disc: "", otherExp: "",
    checking: "", investments: "", retirement: "", otherAssets: "",
    mortgage: "", creditCards: "", autoLoans: "", studentLoans: "",
    termLife: "", existingDB: "", dependents: "2",
  });
  const set    = k => v => setD(p => ({ ...p, [k]: v }));
  const scroll = () => setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth" }), 30);

  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = FONT;
    document.head.appendChild(l);
  }, []);

  const advance = () => {
    if (phase === "landing") { setPhase("steps"); setStepIdx(1); scroll(); return; }
    if (stepIdx < 4) { setStepIdx(s => s + 1); scroll(); return; }
    setPhase("processing");
    setTimeout(() => { setResult(scoreSystem(d)); setPhase("results"); scroll(); }, 2600);
  };
  const back  = () => { if (stepIdx > 1) { setStepIdx(s => s - 1); scroll(); } };
  const reset = () => { setPhase("landing"); setStepIdx(1); setResult(null); scroll(); };

  const base = { minHeight: "100vh", background: B.bg, color: B.white, fontFamily: "'Barlow',sans-serif" };

  // ── LANDING ───────────────────────────────────────────────────────────────
  if (phase === "landing") return (
    <div style={base} className="screen-wrap ris-landing-screen">
      <style>{`
        *{box-sizing:border-box}
        input::-webkit-outer-spin-button,input::-webkit-inner-spin-button{-webkit-appearance:none}
        input[type=number]{-moz-appearance:textfield}
        input::placeholder{color:#1A3060}
        @keyframes fu{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        .a1{animation:fu .5s .05s both}.a2{animation:fu .5s .18s both}
        .a3{animation:fu .5s .32s both}.a4{animation:fu .5s .46s both}
        .pb:hover{background:#1A6BC4!important}
        @media(max-width:600px){
          .a2{font-size:clamp(36px,10vw,68px)!important;}
        }
      `}</style>
      <Header/>
      <div ref={topRef} className="ris-landing-wrap">
        <div style={{ position: "absolute", top: "60px", right: "0", width: "2px", height: "320px", background: `linear-gradient(180deg,${B.blue},transparent)`, opacity: 0.35 }}/>
        <div className="a1" style={{ fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", color: B.blue, fontWeight: "600", marginBottom: "14px" }}>
          Cash Flow Assessment
        </div>
        <h1 className="a2 ris-landing-h1" style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "clamp(38px,7vw,68px)", fontWeight: "800", lineHeight: 1.05, letterSpacing: "-0.01em", marginBottom: "22px", color: B.white }}>
          YOU DON'T HAVE<br/><span style={{ color: B.blue }}>A MONEY PROBLEM.</span>
        </h1>
        <p className="a3 ris-landing-sub" style={{ fontSize: "17px", lineHeight: "1.7", color: B.textSub, fontWeight: "300", marginBottom: "14px" }}>
          Most people assume the answer is earning more. But the real issue is almost never the amount — it's the <strong style={{ color: B.white, fontWeight: "500" }}>system</strong> your money moves through.
        </p>
        <p className="a3 ris-landing-desc" style={{ fontSize: "15px", lineHeight: "1.65", color: B.textMuted, marginBottom: "42px" }}>
          This 5-minute assessment scores your cash flow and identifies the three structural problems that keep most households financially stuck — regardless of income.
        </p>
        <div className="a4" style={{ display: "flex", gap: "28px", marginBottom: "40px", flexWrap: "wrap", justifyContent: "center" }}>
          {[["~5 minutes","Step-by-step"],["No account needed","Completely private"],["Instant results","Based on your numbers"]].map(([t, s]) => (
            <div key={t} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: B.blue, flexShrink: 0 }}/>
              <div>
                <div style={{ fontSize: "13px", fontWeight: "500", color: B.white }}>{t}</div>
                <div style={{ fontSize: "11px", color: B.textDim }}>{s}</div>
              </div>
            </div>
          ))}
        </div>
        <button className="pb a4 ris-start-btn" onClick={advance} style={{ background: B.blue, color: B.white, border: "none", padding: "14px 36px", borderRadius: "5px", fontSize: "15px", fontWeight: "600", cursor: "pointer", fontFamily: "'Barlow',sans-serif", letterSpacing: "0.04em", transition: "background 0.2s" }}>
          Start the Assessment →
        </button>
      </div>
    </div>
  );

  // ── PROCESSING ────────────────────────────────────────────────────────────
  if (phase === "processing") return (
    <div style={{ ...base }} className="screen-wrap">
      <style>{`@keyframes sp{to{transform:rotate(360deg)}}@keyframes pu{0%,100%{opacity:.3}50%{opacity:1}}`}</style>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: "48px", height: "48px", margin: "0 auto 24px", border: `3px solid ${B.border}`, borderTop: `3px solid ${B.blue}`, borderRadius: "50%", animation: "sp .85s linear infinite" }}/>
        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "22px", fontWeight: "600", color: B.white, letterSpacing: "0.04em", marginBottom: "8px" }}>
          ANALYZING YOUR CASH FLOW SYSTEM
        </div>
        <div style={{ color: B.textMuted, fontSize: "13px", animation: "pu 2s ease infinite", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Identifying the three problems
        </div>
      </div>
    </div>
  );

  // ── INPUT STEPS ───────────────────────────────────────────────────────────
  if (phase === "steps") {
    const meta = [
      {
        title: "WHAT COMES IN",
        desc: "Your gross monthly income before taxes. We use this as the foundation — it's what your household depends on and what needs to be protected.",
        note: "Use your gross (before-tax) income. This is what matters for insurance coverage and financial planning."
      },
      { title: "WHERE IT GOES",  desc: "Your regular monthly expenses. Estimates are fine — this is a diagnostic, not a tax return." },
      {
        title: "WHAT YOU HAVE",
        desc: "Your current financial assets broken down by accessibility. This matters because not all money is equally available to you.",
        note: "Liquid assets = money you can access immediately without penalty. Restricted assets = retirement accounts, locked-up equity."
      },
      { title: "WHAT YOU OWE",   desc: "Outstanding balances and your existing life insurance coverage." },
    ];
    const { title, desc, note } = meta[stepIdx - 1];
    return (
      <div style={base} className="screen-wrap">
        <style>{`
          *{box-sizing:border-box}
          input::-webkit-outer-spin-button,input::-webkit-inner-spin-button{-webkit-appearance:none}
          input[type=number]{-moz-appearance:textfield}
          input::placeholder{color:#1A3060}
          input:focus{border-color:${B.blue}!important}
          @keyframes fu{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
          .sf{animation:fu .3s ease both}
          .nb:hover{background:#1A6BC4!important}
          .bb:hover{border-color:${B.blue}!important;color:${B.blue}!important}
          @media(max-width:480px){
            .step-info{padding:32px 20px 56px!important;}
            .step-card-wrap{padding:0 20px!important;}
          }
        `}</style>
        <Header subtitle={`Step ${stepIdx} of 4`}/>
        <div ref={topRef} style={{ maxWidth: "560px", margin: "0 auto", padding: "clamp(24px,5vw,44px) clamp(16px,5vw,28px) 72px" }}>
          <ProgressBar step={stepIdx - 1} total={4}/>
          <div className="sf" key={stepIdx}>
            <div style={{ marginBottom: "22px" }}>
              <div style={{ fontSize: "10px", color: B.textDim, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>Step {stepIdx} of 4</div>
              <h2 style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "30px", fontWeight: "700", color: B.white, marginBottom: "8px", letterSpacing: "0.02em" }}>{title}</h2>
              <p style={{ fontSize: "clamp(13px,3.5vw,14px)", color: B.textMuted, margin: 0, lineHeight: "1.6" }}>{desc}</p>
              {note && (
                <div style={{ marginTop: "10px", background: B.bluePale, border: `1px solid ${B.blueDim}`, borderRadius: "5px", padding: "10px 14px", fontSize: "12px", color: B.textSub, lineHeight: "1.5", wordBreak: "break-word" }}>
                  ℹ️ {note}
                </div>
              )}
            </div>
            <Card>
              {stepIdx === 1 && (
                <Field label="Gross Monthly Income" hint="before taxes" value={d.income} onChange={set("income")}/>
              )}
              {stepIdx === 2 && (<>
                <Field label="Housing" hint="rent or mortgage payment" value={d.housing} onChange={set("housing")}/>
                <Field label="Utilities" value={d.utilities} onChange={set("utilities")}/>
                <Field label="Food & Groceries" value={d.food} onChange={set("food")}/>
                <Field label="Transportation" value={d.transport} onChange={set("transport")}/>
                <Field label="Insurance" hint="health, life, other" value={d.insurance} onChange={set("insurance")}/>
                <Field label="Debt Payments" hint="all monthly minimums" value={d.debtPmt} onChange={set("debtPmt")}/>
                <Field label="Discretionary" hint="dining, subscriptions, entertainment" value={d.disc} onChange={set("disc")}/>
                <Field label="Other Expenses" value={d.otherExp} onChange={set("otherExp")}/>
              </>)}
              {stepIdx === 3 && (<>
                <div style={{ fontSize: "10px", letterSpacing: "0.06em", textTransform: "uppercase", color: B.green, marginBottom: "8px", fontWeight: "600" }}>
                  Liquid Assets — Immediately Accessible
                </div>
                <Field label="Checking & Savings" hint="access within 48 hours, no penalty" value={d.checking} onChange={set("checking")}/>
                <Field label="Brokerage / Investment Accounts" hint="non-retirement, sellable within days" value={d.investments} onChange={set("investments")}/>
                <div style={{ height: "1px", background: B.border, margin: "6px 0 14px" }}/>
                <div style={{ fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: B.yellow, marginBottom: "8px", fontWeight: "600" }}>
                  Restricted Assets — Limited Access
                </div>
                <Field label="Retirement Accounts" hint="401k, IRA — penalties apply before 59½" value={d.retirement} onChange={set("retirement")}/>
                <Field label="Other Assets" hint="home equity, business value, other" value={d.otherAssets} onChange={set("otherAssets")}/>
              </>)}
              {stepIdx === 4 && (<>
                <Field label="Mortgage Balance" value={d.mortgage} onChange={set("mortgage")}/>
                <Field label="Credit Card Balances" value={d.creditCards} onChange={set("creditCards")}/>
                <Field label="Auto Loans" value={d.autoLoans} onChange={set("autoLoans")}/>
                <Field label="Student Loans" value={d.studentLoans} onChange={set("studentLoans")}/>
                <div style={{ height: "1px", background: B.border, margin: "6px 0 14px" }}/>
                <div style={{ fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: B.textDim, marginBottom: "10px" }}>Existing Life Insurance</div>
                <div className="ris-two-col">
                  <Field label="Term Life Coverage" value={d.termLife} onChange={set("termLife")}/>
                  <Field label="Permanent Coverage" value={d.existingDB} onChange={set("existingDB")}/>
                </div>
                <Field label="Dependents" hint="people depending on your income" value={d.dependents} onChange={set("dependents")} prefix="" placeholder="2"/>
              </>)}
            </Card>
            <div className="ris-nav-buttons" style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
              {stepIdx > 1 && (
                <button className="bb" onClick={back} style={{ flex: 1, background: "transparent", border: `1px solid ${B.border}`, color: B.textMuted, padding: "12px", borderRadius: "5px", fontSize: "14px", cursor: "pointer", fontFamily: "'Barlow',sans-serif", transition: "all .15s" }}>
                  ← Back
                </button>
              )}
              <button className="nb" onClick={advance} style={{ flex: stepIdx > 1 ? 2 : 1, background: B.blue, color: B.white, border: "none", padding: "12px", borderRadius: "5px", fontSize: "14px", fontWeight: "600", cursor: "pointer", fontFamily: "'Barlow',sans-serif", transition: "background .2s" }}>
                {stepIdx < 4 ? "Continue →" : "See My Results →"}
              </button>
            </div>
            <div style={{ marginTop: "10px", fontSize: "11px", color: B.textDim, textAlign: "center" }}>
              Estimates are fine — this is a diagnostic, not a tax return
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── RESULTS ───────────────────────────────────────────────────────────────
  if (phase === "results" && result) {
    const r = result;
    const overallLabel = r.overall >= 80 ? "Strong" : r.overall >= 65 ? "Moderate" : r.overall >= 45 ? "At Risk" : "Critical";
    const sevCol = { critical: B.red, significant: B.yellow, moderate: B.blue, low: B.green };

    const problems = [
      {
        id: "debt",
        number: "01",
        title: "THE DEBT DRAIN",
        severity: r.debtSeverity,
        headline: r.debtBurden > 0
          ? `${(r.debtBurden * 100).toFixed(0)}% of income committed before you choose`
          : "Debt burden appears low",
        detail: r.debtBurden > 0.15
          ? `${fmtF(r.debtPmt || 0)}/month leaves your household before you make a single financial decision. That's ${fmt(r.annualInterest)} in interest per year flowing to lenders — capital that can't build your position.`
          : `Your debt payments are a relatively small portion of income. The question is whether the remaining capital is being deployed as efficiently as it could be.`,
        score: r.debtScore,
      },
      {
        id: "liquidity",
        number: "02",
        title: "THE LIQUIDITY TRAP",
        severity: r.liqSeverity,
        headline: r.monthsCovered < 1
          ? "Less than 1 month of accessible cash"
          : `${r.monthsCovered.toFixed(1)} months of truly liquid coverage`,
        detail: r.lockedPct > 0.4
          ? `${Math.round(r.lockedPct * 100)}% of your saved capital sits in retirement accounts you can't reach without penalties or permission. You have money — but the structure limits when and how you can use it.`
          : `Your liquid coverage sits at ${r.monthsCovered.toFixed(1)} months. Most conventional advice suggests 3–6 months — and even that doesn't account for the opportunity cost of money sitting idle.`,
        score: r.liqScore,
      },
      {
        id: "protection",
        number: "03",
        title: "THE PROTECTION GAP",
        severity: r.protSeverity,
        headline: r.protectionGap > 0
          ? `${fmt(r.protectionGap)} gap between need and coverage`
          : "Coverage appears adequate",
        detail: r.protectionGap > 100000
          ? `Based on replacing your full gross income, outstanding debt, and ${r.dependents} dependent${r.dependents !== 1 ? "s" : ""}, your family would face a ${fmt(r.protectionGap)} shortfall. That gap exists today — not someday.`
          : `Your current coverage is close to the estimated need. The question is whether that coverage is structured in a way that also builds your financial position while you're alive.`,
        score: r.protScore,
      },
    ];

    return (
      <div style={base} className="screen-wrap">
        <style>{`
          *{box-sizing:border-box}
          @keyframes fu{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
          .r1{animation:fu .4s .05s both}.r2{animation:fu .4s .2s both}
          .r3{animation:fu .4s .35s both}.r4{animation:fu .4s .5s both}
          .r5{animation:fu .4s .65s both}
          .pb:hover{background:#1A6BC4!important}
          .sb:hover{background:${B.bluePale}!important;border-color:${B.blue}!important;color:${B.blueHi}!important}
          .rst:hover{color:${B.textMuted}!important}
          @media(max-width:480px){
            .results-wrap{padding:32px 20px 60px!important;}
          }
        `}</style>
        <Header subtitle="Cash Flow Assessment — Results"/>
        <div ref={topRef} style={{ maxWidth: "680px", margin: "0 auto", padding: "clamp(24px,5vw,44px) clamp(16px,4vw,28px) 80px" }}>

          <div className="r1" style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: B.blue, fontWeight: "600", marginBottom: "8px" }}>
            YOUR RESULTS
          </div>

          {/* Overall score */}
          <Card className="r1 ris-score-header" accent style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }}>
            <ScoreRing score={r.overall} label="Overall" size={115}/>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <div className="ris-results-title" style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "clamp(20px,5vw,28px)", fontWeight: "700", color: B.white, marginBottom: "8px", letterSpacing: "0.02em" }}>
                {overallLabel.toUpperCase()} CASH FLOW SYSTEM
              </div>
              <div style={{ fontSize: "13px", color: B.textSub, lineHeight: "1.65" }}>
                Your score reflects the structure of your financial system — not just your income or your effort. Below are the three structural problems your numbers reveal.
              </div>
              <div style={{ marginTop: "10px", fontSize: "13px", color: r.surplus >= 0 ? B.green : B.red }}>
                Monthly {r.surplus >= 0 ? "surplus" : "deficit"}: <strong>{fmt(Math.abs(r.surplus))}</strong>
                <span style={{ color: B.textDim, fontSize: "11px", marginLeft: "8px" }}>(est. after taxes)</span>
              </div>
            </div>
          </Card>

          {/* Three problems */}
          <div className="r2" style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: B.textMuted, marginBottom: "12px" }}>
              The Three Problems In Your Numbers
            </div>
            {problems.map(p => (
              <div key={p.id} style={{ background: B.surface, border: `1px solid ${B.border}`, borderLeft: `4px solid ${sevCol[p.severity]}`, borderRadius: "6px", padding: "18px 20px", marginBottom: "10px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "13px", color: B.textDim, letterSpacing: "0.1em" }}>{p.number}</span>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "18px", fontWeight: "700", color: B.white, letterSpacing: "0.04em" }}>{p.title}</span>
                  </div>
                  <SeverityBadge severity={p.severity}/>
                </div>
                <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "15px", color: sevCol[p.severity], marginBottom: "8px" }}>
                  {p.headline}
                </div>
                <div style={{ fontSize: "13px", color: B.textSub, lineHeight: "1.65" }}>
                  {p.detail}
                </div>
              </div>
            ))}
          </div>

          {/* Expense bars */}
          {r.expRatios.length > 0 && (
            <Card className="r3" style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "10px", letterSpacing: "0.12em", textTransform: "uppercase", color: B.textMuted, marginBottom: "14px" }}>Where Your Income Goes</div>
              {r.expRatios.sort((a, b) => b.v - a.v).map(({ label, v, pct }) => (
                <div key={label} style={{ marginBottom: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                    <span style={{ fontSize: "13px", color: B.white }}>{label}</span>
                    <span style={{ fontSize: "12px", color: B.textMuted }}>{fmt(v)} <span style={{ color: B.textDim }}>· {pct.toFixed(1)}%</span></span>
                  </div>
                  <div style={{ height: "3px", background: B.border, borderRadius: "1px" }}>
                    <div style={{ height: "3px", borderRadius: "1px", width: `${Math.min(100, pct)}%`, background: pct > 35 ? B.red : pct > 20 ? B.yellow : B.blue, transition: "width 1s ease" }}/>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${B.border}`, display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "13px", color: B.textMuted }}>Total Monthly Expenses</span>
                <span style={{ fontSize: "14px", fontWeight: "600", color: B.white }}>{fmt(r.totalExp)}</span>
              </div>
            </Card>
          )}

          {/* Insights */}
          <div className="r4" style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "10px", letterSpacing: "0.12em", textTransform: "uppercase", color: B.textMuted, marginBottom: "10px" }}>What This Reveals</div>
            {r.insights.map((ins, i) => <InsightPill key={i} text={ins.text} type={ins.type}/>)}
          </div>

          {/* BRIDGE PROMPT — replaces old two-tier CTA */}
          <Card className="r5" accent style={{ background: `linear-gradient(135deg, ${B.surfaceHi}, ${B.surface})`, borderColor: B.borderHi, marginBottom: "14px" }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "22px", fontWeight: "700", color: B.white, marginBottom: "10px", letterSpacing: "0.02em" }}>
              BEFORE YOU GO — ONE MORE THING.
            </div>
            <div style={{ fontSize: "14px", color: B.textSub, lineHeight: "1.70", marginBottom: "20px", maxWidth: "500px" }}>
              Your scores show where the pressure is. But they don't show you why it's structural — or what a different system actually looks like. Most people who see these numbers have never been shown an alternative. The next screen does that, in plain language, without jargon or a pitch.
            </div>
            <button
              className="sb"
              onClick={() => { setPhase("bridge"); scroll(); }}
              style={{ width: "100%", background: B.blue, border: `1px solid ${B.blue}`, color: B.white, padding: "14px 28px", borderRadius: "5px", fontSize: "15px", fontWeight: "600", cursor: "pointer", fontFamily: "'Barlow',sans-serif", letterSpacing: "0.04em", transition: "all .2s", textAlign: "center" }}
            >
              See What a Different System Looks Like →
            </button>
            <div style={{ marginTop: "8px", fontSize: "11px", color: B.textDim, textAlign: "center" }}>
              Takes 2 minutes · No obligation · Booking a call is optional
            </div>
          </Card>

          <button className="rst" onClick={reset} style={{ background: "none", border: "none", color: B.textDim, fontSize: "12px", cursor: "pointer", display: "block", margin: "16px auto 0", fontFamily: "'Barlow',sans-serif", transition: "color .15s" }}>
            ← Start over
          </button>
        </div>
      </div>
    );
  }

  // ── BRIDGE SCREEN ─────────────────────────────────────────────────────────
  if (phase === "bridge") return (
    <div style={base}>
      <style>{`
        *{box-sizing:border-box}
        @keyframes fu{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        .b1{animation:fu .45s .05s both}.b2{animation:fu .45s .2s both}
        .b3{animation:fu .45s .35s both}.b4{animation:fu .45s .5s both}
        .b5{animation:fu .45s .65s both}
        .vb:hover{background:#1A6BC4!important}
        .cb:hover{border-color:${B.blue}!important;color:${B.blueHi}!important}
        .bk:hover{color:${B.textMuted}!important}
      `}</style>
      <Header subtitle="Understanding the Problem"/>
      <div ref={topRef} className="ris-bridge-wrap">

        {/* Beat 1 — Absolution */}
        <div className="b1" style={{ marginBottom: "36px" }}>
          <div style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: B.blue, fontWeight: "600", marginBottom: "14px" }}>
            First, Something Important
          </div>
          <h2 className="ris-bridge-h2" style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "clamp(20px,6vw,34px)", fontWeight: "700", color: B.white, marginBottom: "14px", lineHeight: 1.15, letterSpacing: "0.01em" }}>
            THERE'S NOTHING WRONG WITH HOW<br/>YOU'VE BEEN MANAGING YOUR MONEY.
          </h2>
          <p style={{ fontSize: "15px", lineHeight: "1.75", color: B.textSub, margin: 0 }}>
            You've been doing what most people do — what most financial advisors recommend. Earn, budget, save what's left, pay down debt, put money in a 401k, carry a life insurance policy. It's responsible, conventional thinking. The question isn't whether you've been doing it wrong. The question is whether there's a more efficient system available — one that solves the three problems you just identified without requiring you to earn more or spend less.
          </p>
        </div>

        <div className="b2" style={{ height: "1px", background: B.border, marginBottom: "36px" }}/>

        {/* Beat 2 — Structural insight */}
        <div className="b2" style={{ marginBottom: "36px" }}>
          <div style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: B.blue, fontWeight: "600", marginBottom: "14px" }}>
            Here's What Most People Don't Realize
          </div>
          <Card accent style={{ marginBottom: "0" }}>
            <p style={{ fontSize: "15px", lineHeight: "1.75", color: B.white, margin: "0 0 14px", fontWeight: "500" }}>
              Every dollar you spend is being financed by someone.
            </p>
            <p style={{ fontSize: "14px", lineHeight: "1.75", color: B.textSub, margin: "0 0 14px" }}>
              Either you pay interest to a lender — or you give up the interest that dollar could have earned. There's no neutral position. The conventional financial system just doesn't make that visible. It focuses your attention on the <em>rate</em> of return on the small slice you're saving, while the much larger volume of interest flowing out of your household every month goes unexamined.
            </p>
            <p style={{ fontSize: "14px", lineHeight: "1.75", color: B.textSub, margin: 0 }}>
              That's the structural problem the three scores you just saw are measuring. Not your income. Not your discipline. The system itself.
            </p>
          </Card>
        </div>

        {/* Beat 3 — The alternative */}
        <div className="b3" style={{ marginBottom: "36px" }}>
          <div style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: B.blue, fontWeight: "600", marginBottom: "14px" }}>
            What a Different System Looks Like
          </div>
          <p style={{ fontSize: "15px", lineHeight: "1.75", color: B.textSub, marginBottom: "20px" }}>
            Instead of saving what's left after everyone else gets paid, imagine redirecting that same money into a private system you own and control — one where your capital stays liquid and accessible, keeps growing even while you use it, and where you set the terms. Not a bank. Not a lender. Not a retirement account with a penalty clause. You.
          </p>
          <div className="ris-compare-grid">
            {[
              {
                label: "Conventional System",
                col: B.red, bg: B.redPale, icon: "×",
                items: ["Earn → spend → save what's left", "Lenders set the terms", "Access requires permission or penalties", "Interest flows out permanently"],
              },
              {
                label: "A Different System",
                col: B.blue, bg: B.bluePale, icon: "→",
                items: ["Redirect capital into what you own", "You set the terms", "Access is immediate, no approval needed", "Interest stays inside your system"],
              },
            ].map(({ label, col, bg, icon, items }) => (
              <div key={label} style={{ background: bg, border: `1px solid ${col}25`, borderRadius: "6px", padding: "16px" }}>
                <div style={{ fontSize: "11px", fontWeight: "600", color: col, marginBottom: "12px", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</div>
                {items.map(item => (
                  <div key={item} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px" }}>
                    <span style={{ color: col, fontSize: "11px", marginTop: "2px", flexShrink: 0 }}>{icon}</span>
                    <span style={{ fontSize: "clamp(11px,3vw,12px)", color: B.textSub, lineHeight: "1.5" }}>{item}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Beat 4 — IBC named once */}
        <div className="b4" style={{ marginBottom: "40px" }}>
          <div style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: B.blue, fontWeight: "600", marginBottom: "14px" }}>
            That System Has a Name
          </div>
          <Card style={{ background: B.surfaceHi, borderColor: B.borderHi }}>
            <p style={{ fontSize: "16px", lineHeight: "1.75", color: B.white, margin: "0 0 12px" }}>
              It's called <strong style={{ color: B.blue }}>Infinite Banking Concept (IBC)</strong>.
            </p>
            <p style={{ fontSize: "14px", lineHeight: "1.75", color: B.textSub, margin: "0 0 12px" }}>
              It's not an investment product. It's not a retirement account. It's not something you've probably been offered before. It's a way of thinking about money — and a specific, time-tested vehicle that makes that thinking practical for everyday households.
            </p>
            <p style={{ fontSize: "14px", lineHeight: "1.75", color: B.textSub, margin: 0 }}>
              Nate put together a short video that explains how it works in plain language. Watch it — then decide if a conversation makes sense.
            </p>
          </Card>
        </div>

        {/* Bridge CTAs */}
        <div className="b5" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

          {/* Primary: Video */}
          <a href={VIDEO_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <button className="vb" style={{ width: "100%", background: B.blue, color: B.white, border: "none", padding: "14px 28px", borderRadius: "5px", fontSize: "clamp(14px,4vw,15px)", fontWeight: "600", cursor: "pointer", fontFamily: "'Barlow',sans-serif", letterSpacing: "0.04em", transition: "background 0.2s", textAlign: "center" }}>
              Watch Nate's Video — How It Works →
            </button>
          </a>
          <div style={{ fontSize: "11px", color: B.textDim, textAlign: "center" }}>
            A short breakdown of IBC in plain language — no jargon, no pitch
          </div>

          {/* Secondary: Book a call */}
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <button className="cb" style={{ width: "100%", background: "transparent", border: `1px solid ${B.border}`, color: B.textSub, padding: "13px 28px", borderRadius: "5px", fontSize: "14px", fontWeight: "500", cursor: "pointer", fontFamily: "'Barlow',sans-serif", letterSpacing: "0.02em", transition: "all .15s", textAlign: "center" }}>
              Ready to Talk? Book a Call with Kevin →
            </button>
          </a>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "4px 0" }}>
            <div style={{ flex: 1, height: "1px", background: B.border }}/>
            <span style={{ fontSize: "11px", color: B.textDim, letterSpacing: "0.08em", textTransform: "uppercase" }}>or</span>
            <div style={{ flex: 1, height: "1px", background: B.border }}/>
          </div>

          {/* Tertiary: Mini-course */}
          <div style={{ background: B.bluePale, border: `1px solid ${B.blueDim}`, borderRadius: "6px", padding: "18px 20px", boxSizing: "border-box", width: "100%" }}>
            <div style={{ fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: B.blue, fontWeight: "600", marginBottom: "6px" }}>
              Free Mini-Course
            </div>
            <div style={{ fontSize: "14px", color: B.white, fontWeight: "500", marginBottom: "4px" }}>
              5 Cash Flow Principles That Change Everything
            </div>
            <div style={{ fontSize: "12px", color: B.textMuted, lineHeight: "1.55", marginBottom: "14px" }}>
              Not ready to talk yet? Get the free 5-part email series that explains the principles behind everything you just saw — in plain language, delivered weekly to your inbox.
            </div>
            <a href="https://richeyinsurancesolutions.com/mini-course" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              <button style={{ width: "100%", background: "transparent", border: `1px solid ${B.blue}`, color: B.blue, padding: "11px 20px", borderRadius: "5px", fontSize: "13px", fontWeight: "600", cursor: "pointer", fontFamily: "'Barlow',sans-serif", letterSpacing: "0.04em", transition: "all .15s", textAlign: "center" }}>
                Get the Free Mini-Course →
              </button>
            </a>
            <div style={{ fontSize: "11px", color: B.textDim, textAlign: "center", marginTop: "8px" }}>
              Free · No spam · Unsubscribe anytime
            </div>
          </div>

        </div>

        <button className="bk" onClick={() => { setPhase("results"); scroll(); }} style={{ background: "none", border: "none", color: B.textDim, fontSize: "12px", cursor: "pointer", display: "block", margin: "24px auto 0", fontFamily: "'Barlow',sans-serif", transition: "color .15s" }}>
          ← Back to my results
        </button>
      </div>
    </div>
  );

  return null;
}
